import { useState, useEffect } from "react";
import axios from "axios";
import "./ConstructionCostEstimate.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
const PYEONG_TO_SQM = 3.3058; // 1坪 = 3.3058㎡

function ConstructionCostEstimate() {
  const [luseList, setLuseList] = useState([]);
  const [selectedLuse, setSelectedLuse] = useState("");
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [filteredBuildingTypes, setFilteredBuildingTypes] = useState([]);
  const [costEstimates, setCostEstimates] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLuseList();
    fetchBuildingTypes();
  }, []);

  useEffect(() => {
    if (selectedLuse && buildingTypes.length > 0) {
      filterBuildingTypesByLuse();
    }
  }, [selectedLuse, buildingTypes]);

  useEffect(() => {
    if (filteredBuildingTypes.length > 0) {
      calculateCostEstimates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBuildingTypes]);

  const fetchLuseList = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/dubai/zoning/luse-list`);
      setLuseList(response.data || []);
    } catch (error) {
      console.error("Error fetching luse list:", error);
      alert("Failed to load zoning list.");
    }
  };

  const fetchBuildingTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/building-types`);
      setBuildingTypes(response.data);
    } catch (error) {
      console.error("Error fetching building types:", error);
      alert("Failed to load building types.");
    } finally {
      setLoading(false);
    }
  };

  const filterBuildingTypesByLuse = () => {
    const filtered = buildingTypes.filter((type) => {
      return (
        type.applicableZoning &&
        type.applicableZoning.length > 0 &&
        type.applicableZoning.includes(selectedLuse)
      );
    });
    setFilteredBuildingTypes(filtered);
  };

  const calculateCostEstimates = async () => {
    try {
      setLoading(true);
      const estimates = {};

      for (const buildingType of filteredBuildingTypes) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/admin/construction-costs`,
            {
              params: { buildingType: buildingType.code },
            }
          );

          const costs = response.data.costs || [];
          
          // 모든 공종의 totalCost 합산 (NT$/㎡)
          const totalCostPerSqm = costs.reduce((sum, cost) => {
            return sum + (cost.totalCost || 0);
          }, 0);

          // 평당 공사비 계산 (원/坪)
          const totalCostPerPyeong = totalCostPerSqm * PYEONG_TO_SQM;

          estimates[buildingType.code] = {
            buildingType,
            costs,
            totalCostPerSqm,
            totalCostPerPyeong,
            costCount: costs.length,
          };
        } catch (error) {
          console.error(
            `Error fetching costs for ${buildingType.code}:`,
            error
          );
          estimates[buildingType.code] = {
            buildingType,
            costs: [],
            totalCostPerSqm: 0,
            totalCostPerPyeong: 0,
            costCount: 0,
            error: true,
          };
        }
      }

      setCostEstimates(estimates);
    } catch (error) {
      console.error("Error calculating cost estimates:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "N/A";
    return "AED " + new Intl.NumberFormat("en-AE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(value));
  };

  return (
    <div className="construction-cost-estimate">
      <div className="page-header">
        <h1>Area-Based Construction Cost Estimate</h1>
      </div>

      <div className="luse-selector-section">
        <div className="selector-card">
          <label>Zoning Selection:</label>
          <select
            value={selectedLuse}
            onChange={(e) => setSelectedLuse(e.target.value)}
            className="luse-select"
          >
            <option value="">Please select zoning</option>
            {luseList.map((luse) => (
              <option key={luse} value={luse}>
                {luse}
              </option>
            ))}
          </select>
        </div>

        {selectedLuse && (
          <div className="info-card">
            <p>
              <strong>Selected Zoning:</strong> {selectedLuse}
            </p>
            <p>
              <strong>Applicable Building Types:</strong>{" "}
              {filteredBuildingTypes.length}
            </p>
          </div>
        )}
      </div>

      {loading && (
        <div className="loading">Calculating construction costs...</div>
      )}

      {selectedLuse && !loading && filteredBuildingTypes.length === 0 && (
        <div className="empty-state">
          <p>The selected zoning has no applicable building types.</p>
        </div>
      )}

      {selectedLuse &&
        !loading &&
        filteredBuildingTypes.length > 0 &&
        filteredBuildingTypes.map((type) => {
          const estimate = costEstimates[type.code];
          if (!estimate) return null;

          return (
            <div key={type.code} className="estimate-card">
              <div className="estimate-header">
                <h3>{type.name}</h3>
                <span className="building-type-code">{type.code}</span>
              </div>

              {estimate.error ? (
                <div className="error-message">
                  Failed to load construction cost information.
                </div>
              ) : (
                <>
                  <div className="estimate-summary">
                    <div className="summary-item highlight">
                      <div className="summary-label">Total Cost per m²</div>
                      <div className="summary-value">
                        {formatCurrency(estimate.totalCostPerSqm)}/㎡
                      </div>
                    </div>
                    <div className="summary-item">
                      <div className="summary-label">Work Types</div>
                      <div className="summary-value">
                        {estimate.costCount}
                      </div>
                    </div>
                  </div>

                  {type.description && (
                    <div className="building-type-description">
                      <strong>Description:</strong> {type.description}
                    </div>
                  )}

                  {estimate.costs.length > 0 && (
                    <div className="cost-details">
                      <h4>Work Type Cost Details</h4>
                      <div className="costs-table-wrapper">
                        <table className="costs-table">
                          <thead>
                            <tr>
                              <th>Order</th>
                              <th>Work Type Name</th>
                              <th>Material Cost</th>
                              <th>Labor Cost</th>
                              <th>Expense Cost</th>
                              <th>Indirect Cost</th>
                              <th>Total (AED/m²)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {estimate.costs.map((cost) => (
                              <tr key={cost.id}>
                                <td>{cost.workOrder}</td>
                                <td>{cost.workName}</td>
                                <td>{formatCurrency(cost.materialCost)}</td>
                                <td>{formatCurrency(cost.laborCost)}</td>
                                <td>{formatCurrency(cost.expenseCost)}</td>
                                <td>{formatCurrency(cost.indirectCost)}</td>
                                <td className="total-cell">
                                  {formatCurrency(cost.totalCost)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="total-row">
                              <td colSpan="6" className="total-label">
                                Total
                              </td>
                              <td className="total-value">
                                {formatCurrency(estimate.totalCostPerSqm)}/㎡
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
    </div>
  );
}

export default ConstructionCostEstimate;

